import { createQueryKeys } from '@lukemorales/query-key-factory';

import type {
  FetchInfiniteOrganizationUsersByRoleArgs,
  FetchCurrentUserByTenantId,
  FetchOrganizationUsersArgs,
  FetchInfiniteUsersArgs,
  FetchCountriesArgs,
  FetchUserByIdArgs,
  FetchAuthorsArgs,
  FetchUsersArgs,
} from '../api/types/user.types';

import {
  fetchOrganizationUsersByRole,
  fetchContentLibraryAuthors,
  fetchMarketplaceAuthors,
  fetchOrganizationUsers,
  fetchLogicalProfiles,
  fetchPublicAuthors,
  fetchCurrentUser,
  fetchCountries,
  fetchUsernameAvailability,
  fetchUserById,
  fetchUsers,
} from '../api';

export const usersQueryKeys = createQueryKeys('users', {
  fetchCurrentUser: ({ token }: FetchCurrentUserByTenantId) => ({
    queryFn: async () => fetchCurrentUser({ token }),
    queryKey: [token],
  }),
  fetchUsernameAvailability: (username: string) => ({
    queryFn: () => fetchUsernameAvailability(username),
    queryKey: [username],
  }),
  fetchLogicalProfiles: () => ({
    queryFn: () => fetchLogicalProfiles(),
    queryKey: ['logical-profiles'],
  }),
  fetchCurrentUserToken: () => ({
    queryKey: ['token'],
  }),
  fetchInfiniteContentLibraryAuthors: ({ filters }: FetchAuthorsArgs) => ({
    queryFn: (context) =>
      fetchContentLibraryAuthors({
        filters: {
          ...filters,
          offset: (context?.pageParam as string) || '1',
          orderBy: filters?.orderBy || 'name',
        },
      }),
    queryKey: [filters],
  }),
  fetchInfiniteCountries: ({ filters }: FetchCountriesArgs) => ({
    queryFn: (context) =>
      fetchCountries({
        filters: {
          ...filters,
          offset: (context?.pageParam as string) || '1',
        },
      }),
    queryKey: [filters],
  }),
  fetchInfiniteMarketplaceAuthors: ({ filters }: FetchAuthorsArgs) => ({
    queryFn: (context) =>
      fetchMarketplaceAuthors({
        filters: {
          ...filters,
          offset: (context?.pageParam as string) || '1',
          orderBy: filters?.orderBy || 'name',
        },
      }),
    queryKey: [filters],
  }),
  fetchInfiniteOrganizationUsers: ({
    filters,
    organizationId,
    types,
  }: FetchOrganizationUsersArgs) => ({
    queryFn: (context) =>
      fetchOrganizationUsers({
        filters: {
          ...filters,
          offset: (context?.pageParam as string) || '1',
          orderBy: filters?.orderBy || 'name',
        },
        organizationId,
        types,
      }),
    queryKey: [organizationId, filters],
  }),
  fetchInfiniteOrganizationUsersByRole: ({
    filters,
    organizationId,
    roles,
  }: FetchInfiniteOrganizationUsersByRoleArgs) => ({
    queryFn: (context) =>
      fetchOrganizationUsersByRole({
        filters: {
          ...filters,
          offset: (context?.pageParam as string) || '1',
          orderBy: filters?.orderBy || 'name',
        },
        organizationId,
        roles,
      }),
    queryKey: [organizationId, filters, roles],
  }),
  fetchInfinitePublicAuthors: ({ filters }: FetchAuthorsArgs) => ({
    queryFn: (context) =>
      fetchPublicAuthors({
        filters: {
          ...filters,
          offset: (context?.pageParam as string) || '1',
          orderBy: filters?.orderBy || 'name',
        },
      }),
    queryKey: [filters],
  }),
  fetchInfiniteUsers: ({
    filters,
    organizationId,
    types,
  }: FetchInfiniteUsersArgs) => ({
    queryFn: (context) =>
      fetchUsers({
        filters: {
          ...filters,
          offset: (context?.pageParam as string) || '1',
          orderBy: filters?.orderBy || 'name',
        },
        organizationId,
        types,
      }),
    queryKey: [organizationId, filters, types],
  }),
  fetchOrganizationUsers: ({
    filters,
    organizationId,
  }: FetchOrganizationUsersArgs) => ({
    queryFn: () => fetchOrganizationUsers({ filters, organizationId }),
    queryKey: [organizationId, filters],
  }),
  fetchUserById: ({ id, organizationId }: FetchUserByIdArgs) => ({
    queryFn: async () => fetchUserById({ id, organizationId }),
    queryKey: [organizationId, id],
  }),
  fetchUsers: ({
    filters,
    membershipIds,
    organizationId,
    types,
    withoutTrainer,
  }: FetchUsersArgs) => ({
    queryFn: () =>
      fetchUsers({
        filters,
        membershipIds,
        organizationId,
        types,
        withoutTrainer,
      }),
    queryKey: [organizationId, filters, types, withoutTrainer, membershipIds],
  }),
});
